import { ViewIds } from '@ardoq/api-types';
import { getFieldDropdownOptions } from '@ardoq/settings-bar';
import {
  type SettingsConfig,
  SettingsType,
  updateViewSettings,
} from '@ardoq/view-settings';
import { dispatchAction } from '@ardoq/rxbeach';
import { DropdownOptionType } from '@ardoq/dropdown-menu';
import { IconName } from '@ardoq/icons';
import { t, type I18nLocale } from '@ardoq/i18n';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { createDropdownSliderOptionManager } from 'viewSettings/settingsHelper';
import { TILING_METHODS } from '../view/standard/consts';
import type {
  HierarchicalTreemapViewpointViewProperties,
  HierarchicalViewpointViewId,
  HierarchicalViewpointViewProperties,
} from './types';

/**
 * The controls a viewpoint-mode hierarchical view offers.
 *
 * There is no traversal control, and no branch that removes one. The workspace-mode bar opens with
 * `dropdownTraversalMenu` — degrees of relationship, reference types to follow, additional
 * traversals — which describes how a workspace tab decides what to draw. A viewpoint decided that
 * before the view was mounted, so the shared version had `traversalMenuItems` return `[]` in this
 * mode: a control built, then thrown away, on every settings emission.
 *
 * Nor is there a right menu. Viewpoint mode's panel owns export and the rest, so
 * `getRightMenuConfig` has nothing to attach to here.
 */
const sizeByFieldMenuItem = (
  viewId: HierarchicalViewpointViewId,
  { sizing, numericFields, i18nLocale }: HierarchicalViewpointViewProperties
): SettingsConfig => ({
  id: 'sizeByFieldName',
  label: t(
    i18nLocale,
    'tabview:hierarchicalTreemap.sizeByField',
    'Size by field'
  ),
  type: SettingsType.DROPDOWN,
  iconName: IconName.FORMAT_LIST_BULLETED,
  options: getFieldDropdownOptions({
    viewId,
    list: numericFields,
    settingsKey: 'sizeByFieldName',
    /* The sizing the hierarchy was summed with, not the setting it came from - the same rule the
       panel row and the number format now follow. A persisted confidential field survives in
       `numericFields`, so `toSizing` refuses it and the row reads `Component count` while the
       dropdown it opens from would tick the refused field's name. */
    isActive: fieldName =>
      sizing.kind === 'field' && sizing.fieldName === fieldName,
    onViewSettingsUpdate,
  }),
});

const maxDepthMenuItem = (
  viewId: HierarchicalViewpointViewId,
  value: number,
  i18nLocale: I18nLocale
): SettingsConfig => ({
  id: 'maxDepthContainer',
  label: t(
    i18nLocale,
    'tabview:hierarchicalTreemap.maximumDepth',
    'Maximum depth'
  ),
  type: SettingsType.DROPDOWN,
  iconName: IconName.COLLAPSE_GROUPS,
  options: [
    createDropdownSliderOptionManager(
      viewId,
      new Map(),
      onViewSettingsUpdate
    )({
      name: 'maxDepth',
      label: t(
        i18nLocale,
        'tabview:hierarchicalTreemap.maximumDepth',
        'Maximum depth'
      ),
      value,
      min: 0,
      max: 10,
      step: 1,
      type: DropdownOptionType.SLIDER,
    }),
  ],
});

/**
 * Tiling, which only the treemap has.
 *
 * On the shared settings type all three views carried `tilingMethod`; the packing and sunburst
 * persisted one and nothing read it. Here it lives on the treemap's own settings type, so the
 * other two cannot hold one.
 */
const tilingMethodMenuItem = (
  i18nLocale: I18nLocale,
  active: string
): SettingsConfig => ({
  id: 'tilingMethod',
  label: t(
    i18nLocale,
    'tabview:hierarchicalTreemap.tilingMethod',
    'Tiling method'
  ),
  type: SettingsType.DROPDOWN,
  iconName: IconName.SQUARE,
  options: Object.keys(TILING_METHODS).map(key => ({
    name: key,
    label: t(
      i18nLocale,
      `tabview:hierarchicalTreemap.tiling.${key}` as Parameters<typeof t>[1],
      TILING_METHODS[key as keyof typeof TILING_METHODS].label
    ),
    isActive: active === key,
    onClick: () =>
      dispatchAction(
        updateViewSettings({
          viewId: ViewIds.HIERARCHICAL_TREEMAP,
          persistent: true,
          settings: { tilingMethod: key },
        })
      ),
    type: DropdownOptionType.OPTION,
  })),
});

export const hierarchicalTreemapViewpointModifiers = (
  properties: HierarchicalTreemapViewpointViewProperties
): SettingsConfig[] => [
  sizeByFieldMenuItem(ViewIds.HIERARCHICAL_TREEMAP, properties),
  tilingMethodMenuItem(
    properties.i18nLocale,
    properties.viewSettings.tilingMethod
  ),
  maxDepthMenuItem(
    ViewIds.HIERARCHICAL_TREEMAP,
    properties.viewSettings.maxDepth,
    properties.i18nLocale
  ),
];

export const hierarchicalPackingViewpointModifiers = (
  properties: HierarchicalViewpointViewProperties
): SettingsConfig[] => [
  sizeByFieldMenuItem(ViewIds.HIERARCHICAL_PACKING, properties),
  maxDepthMenuItem(
    ViewIds.HIERARCHICAL_PACKING,
    properties.viewSettings.maxDepth,
    properties.i18nLocale
  ),
];

export const sunburstViewpointModifiers = (
  properties: HierarchicalViewpointViewProperties
): SettingsConfig[] => [
  sizeByFieldMenuItem(ViewIds.SUNBURST, properties),
  maxDepthMenuItem(
    ViewIds.SUNBURST,
    properties.viewSettings.maxDepth,
    properties.i18nLocale
  ),
];
